/**
 * TagInput — Reusable multi-tag input with autocomplete suggestions.
 * Used for "Skills & Requirements" on both Job and Gig forms.
 *
 * Props:
 *   tags          string[]              — currently added tags
 *   onChange      (tags: string[]) => void
 *   suggestions   string[]              — full autocomplete source list
 *   placeholder   string
 *   error         string                — shows red border when present
 *   maxSuggestions number               (default 8)
 */
import { useState, useRef } from "react";
import { IconTag, IconX } from "@tabler/icons-react";
import styles from "./tagsInput.module.css";

export default function TagInput({
  tags = [],
  onChange,
  suggestions = [],
  placeholder = "Type and press Enter…",
  error,
  maxSuggestions = 8,
}) {
  const [query, setQuery] = useState("");
  const [focusIdx, setFocusIdx] = useState(-1);
  const inputRef = useRef(null);

  const filtered = query.trim()
    ? suggestions
        .filter(
          (s) =>
            s.toLowerCase().includes(query.toLowerCase()) && !tags.includes(s),
        )
        .slice(0, maxSuggestions)
    : [];

  const addTag = (val) => {
    const v = val.trim();
    if (!v || tags.includes(v)) {
      setQuery("");
      return;
    }
    onChange([...tags, v]);
    setQuery("");
    setFocusIdx(-1);
    inputRef.current?.focus();
  };

  const removeTag = (val) => onChange(tags.filter((t) => t !== val));

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusIdx((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusIdx((i) => Math.max(i - 1, -1));
    } else if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      if (focusIdx >= 0 && filtered[focusIdx]) addTag(filtered[focusIdx]);
      else if (query.trim()) addTag(query.replace(",", ""));
    } else if (e.key === "Escape") {
      setQuery("");
      setFocusIdx(-1);
    } else if (e.key === "Backspace" && query === "" && tags.length > 0) {
      removeTag(tags[tags.length - 1]);
    }
  };

  return (
    <div className={styles.wrap}>
      <div
        className={`${styles.tagsField} ${error ? styles.tagsFieldError : ""}`}
        onClick={() => inputRef.current?.focus()}
      >
        {tags.map((tag) => (
          <span key={tag} className={styles.tag}>
            {tag}
            <button
              type="button"
              className={styles.tagRemove}
              aria-label={`Remove ${tag}`}
              onClick={(e) => {
                e.stopPropagation();
                removeTag(tag);
              }}
            >
              <IconX size={10} />
            </button>
          </span>
        ))}

        <input
          ref={inputRef}
          type="text"
          className={styles.input}
          placeholder={tags.length === 0 ? placeholder : ""}
          autoComplete="off"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setFocusIdx(-1);
          }}
          onKeyDown={handleKeyDown}
          onBlur={() =>
            setTimeout(() => {
              setQuery("");
              setFocusIdx(-1);
            }, 150)
          }
        />
      </div>

      {filtered.length > 0 && (
        <div className={styles.dropdown}>
          {filtered.map((s, i) => (
            <div
              key={s}
              className={`${styles.item} ${i === focusIdx ? styles.itemActive : ""}`}
              onMouseDown={(e) => {
                e.preventDefault();
                addTag(s);
              }}
            >
              <IconTag size={14} />
              {s}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
